"use client";

import { c as _c } from "react/compiler-runtime";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { getDefaultQueryRetry } from "@/components/react-query-wrapper/utils/query-utils";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiDropPoll } from "@/generated/models/ApiDropPoll";
import type { ApiPageSortDirection } from "@/generated/models/ApiPageSortDirection";
import type { ApiWavePoll } from "@/generated/models/ApiWavePoll";
import { DropSize, type ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { fetchDropsV2ByIds, fetchWavePollsV2, type ApiWavePollDropRow, type WavePollsSort, type WavePollsState } from "@/services/api/wave-drops-v2-api";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
const WAVE_POLLS_PAGE_SIZE = 20;
interface UseWavePollDropsProps {
  readonly waveId: string;
  readonly sortDirection: ApiPageSortDirection;
  readonly sort: WavePollsSort;
  readonly state?: WavePollsState | undefined;
  readonly enabled?: boolean | undefined;
}
interface WavePollDropsPage {
  readonly drops: ApiDrop[];
  readonly page: number;
  readonly next: boolean;
}
interface NormalizedWavePollRow {
  readonly dropId: string;
  readonly poll: ApiDropPoll | null;
}
type StandaloneWavePollFields = Pick<ApiWavePoll, "id" | "drop_id" | "options" | "voted" | "multichoice" | "closing_time" | "is_open">;
const isNonEmptyString = (value: unknown): value is string => typeof value === "string" && value.trim().length > 0;
const hasStandalonePollFields = (row: ApiWavePollDropRow): row is StandaloneWavePollFields => isNonEmptyString(row.id) && isNonEmptyString(row.drop_id) && Array.isArray(row.options) && Array.isArray(row.voted) && typeof row.multichoice === "boolean" && typeof row.closing_time === "number" && typeof row.is_open === "boolean";
const mapWavePollToDropPoll = (poll: StandaloneWavePollFields): ApiDropPoll => ({
  id: poll.id,
  options: poll.options,
  voted: poll.voted,
  multichoice: poll.multichoice,
  closing_time: poll.closing_time,
  is_open: poll.is_open
});
const normalizeWavePollRow = (row: ApiWavePollDropRow): NormalizedWavePollRow | null => {
  if (isNonEmptyString(row.drop_id)) {
    return {
      dropId: row.drop_id,
      poll: hasStandalonePollFields(row) ? mapWavePollToDropPoll(row) : row.poll ?? null
    };
  }
  if (row.poll && isNonEmptyString(row.id)) {
    return {
      dropId: row.id,
      poll: row.poll
    };
  }
  return null;
};
const normalizeWavePollRows = (rows: readonly ApiWavePollDropRow[]): NormalizedWavePollRow[] => rows.reduce<NormalizedWavePollRow[]>((acc, row) => {
  const normalizedRow = normalizeWavePollRow(row);
  if (normalizedRow) {
    acc.push(normalizedRow);
  }
  return acc;
}, []);
const overlayPollsOnDrops = ({
  drops,
  pollRows
}: {
  readonly drops: readonly ApiDrop[];
  readonly pollRows: readonly NormalizedWavePollRow[];
}): ApiDrop[] => {
  const dropsById = new Map(drops.map(drop => [drop.id, drop]));
  return pollRows.reduce<ApiDrop[]>((acc, pollRow) => {
    const drop = dropsById.get(pollRow.dropId);
    if (!drop) {
      return acc;
    }
    acc.push({
      ...drop,
      ...(pollRow.poll ? {
        poll: pollRow.poll
      } : {})
    });
    return acc;
  }, []);
};
const processPollDrops = (pages: readonly WavePollDropsPage[] | undefined): ExtendedDrop[] => {
  if (!pages) {
    return [];
  }
  const keyCount = new Map<string, number>();
  return pages.flatMap(page => page.drops.map(drop => {
    const count = (keyCount.get(drop.id) ?? 0) + 1;
    keyCount.set(drop.id, count);
    return {
      ...drop,
      type: DropSize.FULL,
      stableKey: count > 1 ? `${drop.id}-${count}` : drop.id,
      stableHash: drop.id
    };
  }));
};
export function useWavePollDrops(t0) {
  const $ = _c(34);
  const {
    waveId,
    sortDirection,
    sort,
    state,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== enabled || $[1] !== waveId) {
    t2 = enabled && waveId.trim().length > 0;
    $[0] = enabled;
    $[1] = waveId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isQueryEnabled = t2;
  const t3 = state ?? null;
  let t4;
  if ($[3] !== sort || $[4] !== sortDirection || $[5] !== t3 || $[6] !== waveId) {
    t4 = [QueryKey.WAVE_POLLS, {
      waveId,
      pageSize: WAVE_POLLS_PAGE_SIZE,
      sortDirection,
      sort,
      state: t3
    }];
    $[3] = sort;
    $[4] = sortDirection;
    $[5] = t3;
    $[6] = waveId;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const queryKey = t4 as const;
  let t5;
  if ($[8] !== sort || $[9] !== sortDirection || $[10] !== state || $[11] !== waveId) {
    t5 = async t6 => {
      const {
        pageParam,
        signal
      } = t6;
      const pollsPage = await fetchWavePollsV2({
        waveId,
        page: pageParam,
        pageSize: WAVE_POLLS_PAGE_SIZE,
        sortDirection,
        sort,
        state,
        signal
      });
      const pollRows = normalizeWavePollRows(pollsPage.data);
      const dropIds = [...new Set(pollRows.map(_temp))];
      const drops = dropIds.length > 0 ? await fetchDropsV2ByIds({
        dropIds,
        signal,
        includeFullMetadata: false,
        includeTopRaters: false
      }) : [];
      return {
        drops: overlayPollsOnDrops({
          drops,
          pollRows
        }),
        page: pollsPage.page,
        next: pollsPage.next
      };
    };
    $[8] = sort;
    $[9] = sortDirection;
    $[10] = state;
    $[11] = waveId;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = getDefaultQueryRetry();
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== isQueryEnabled || $[15] !== queryKey || $[16] !== t5) {
    t7 = {
      queryKey,
      queryFn: t5,
      initialPageParam: 1,
      getNextPageParam: _temp2,
      enabled: isQueryEnabled,
      staleTime: 60000,
      ...t6
    };
    $[14] = isQueryEnabled;
    $[15] = queryKey;
    $[16] = t5;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const {
    data,
    fetchNextPage: onFetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    refetch
  } = useInfiniteQuery(t7);
  let t8;
  if ($[18] !== hasNextPage || $[19] !== isFetchingNextPage || $[20] !== onFetchNextPage) {
    t8 = async () => {
      if (hasNextPage && !isFetchingNextPage) {
        await onFetchNextPage();
      }
    };
    $[18] = hasNextPage;
    $[19] = isFetchingNextPage;
    $[20] = onFetchNextPage;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const fetchNextPage = t8;
  let t9;
  if ($[22] !== data?.pages) {
    t9 = () => processPollDrops(data?.pages);
    $[22] = data?.pages;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  data?.pages;
  let t10;
  if ($[24] !== t9) {
    t10 = t9();
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const drops_0 = t10;
  const hasInitialized = !!data?.pages;
  const t11 = isQueryEnabled && (isFetching || !hasInitialized);
  let t12;
  if ($[26] !== drops_0 || $[27] !== fetchNextPage || $[28] !== hasNextPage || $[29] !== isError || $[30] !== isFetchingNextPage || $[31] !== refetch || $[32] !== t11) {
    t12 = {
      drops: drops_0,
      fetchNextPage,
      hasNextPage,
      isError,
      isFetching: t11,
      isFetchingNextPage,
      refetch
    };
    $[26] = drops_0;
    $[27] = fetchNextPage;
    $[28] = hasNextPage;
    $[29] = isError;
    $[30] = isFetchingNextPage;
    $[31] = refetch;
    $[32] = t11;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
}
function _temp2(lastPage) {
  return lastPage.next ? lastPage.page + 1 : null;
}
function _temp(pollRow) {
  return pollRow.dropId;
}